import apiClient from '@/shared/api/client';
import { ApiResponse, PaginatedResponse } from '@/shared/api/types';
import type { OverviewRange } from './campaignsApi';

export interface AdsMetrics {
  totalSpend: number;
  spendChange: number;
  conversions: number;
  conversionsChange: number;
  cpa: number;
  cpaChange: number;
  roas: number;
  impressions: number;
  ctr: number;
}

export interface AdCampaign {
  id: string;
  marketingCampaignId: string | null;
  name: string;
  platform: 'GOOGLE' | 'META';
  status: 'ACTIVE' | 'PAUSED' | 'ENDED' | 'PENDING';
  budgetType: 'DAILY' | 'LIFETIME';
  budgetAmount: number;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  cpa: number | null;
  roas: number | null;
}

export interface KeywordEntry {
  text: string;
  matchType: 'broad' | 'phrase' | 'exact';
}

export interface KeywordActionData {
  type: 'add_negatives' | 'add_keywords';
  campaignExternalId: string;
  keywords: KeywordEntry[];
  executionResult?: {
    succeeded: string[];
    failed: Array<{ keyword: string; error: string }>;
    executedAt: string;
  };
}

/** Recommendation codes that have executors. Keep in sync with recRegistry.ts. */
export const EXECUTABLE_REC_CODES = ['GADS-SEARCH-KW-001', 'GADS-SEARCH-KW-002'] as const;

export interface AdsRecommendation {
  id: string;
  type: string;
  /** Recommendation code (e.g. 'GADS-KW-001'). Determines executor. */
  category: string;
  title: string;
  description: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  status: 'PENDING' | 'APPROVED' | 'EXECUTING' | 'DONE' | 'FAILED' | 'DISMISSED' | 'SUPERSEDED';
  actionData?: KeywordActionData | Record<string, unknown>;
  executedAt?: string;
}

export const adsApi = {
  getMetrics: async (
    locationId: string,
    range?: OverviewRange,
  ): Promise<ApiResponse<AdsMetrics>> => {
    const response = await apiClient.get<ApiResponse<AdsMetrics>>('/ads/metrics', {
      params: { locationId, ...(range ? { range } : {}) },
    });
    return response.data;
  },

  getCampaigns: async (
    locationId: string,
    range?: OverviewRange,
  ): Promise<PaginatedResponse<AdCampaign>> => {
    const response = await apiClient.get<PaginatedResponse<AdCampaign>>('/ads/campaigns', {
      params: { locationId, ...(range ? { range } : {}) },
    });
    return response.data;
  },

  getRecommendations: async (locationId: string): Promise<ApiResponse<AdsRecommendation[]>> => {
    const response = await apiClient.get<ApiResponse<AdsRecommendation[]>>('/ads/recommendations', {
      params: { locationId },
    });
    return response.data;
  },

  approveRecommendation: async (id: string): Promise<ApiResponse<AdsRecommendation>> => {
    const response = await apiClient.post<ApiResponse<AdsRecommendation>>(
      `/ads/recommendations/${id}/approve`,
    );
    return response.data;
  },

  generateRecommendations: async (
    locationId: string,
    platform: 'google' | 'meta',
  ): Promise<ApiResponse<{ processed: number; skipped: number; recsCreated: number }>> => {
    const response = await apiClient.post<
      ApiResponse<{ processed: number; skipped: number; recsCreated: number }>
    >('/ads/recommendations/generate', { locationId, platform });
    return response.data;
  },
};
